/*
 * Manage Location Form
 * Edit location items
 */

import * as React from 'react';
import { AbstractControl, FormGenerator, FormGroup, Validators } from 'react-reactive-form';
import { Button, Modal, Row } from 'react-bootstrap';
import { Ibuilding, Ifacility, Ifloor, Ilocation, ItableFiltersReducer } from '../../models';
import {
    saveAnyLocation,
    updateAnyLocation,
    toggleEditLocationModal,
    cloneAnyLocationObject
} from '../../actions/manageLocationActions';
import { FormUtil } from '../common/FormUtil';
import { constants } from '../../constants/constants';
import forEach from 'lodash/forEach';
import { toastr } from 'react-redux-toastr';
import { TFunction } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WithTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getIsMobile } from '../../reducers/configReducer';

interface Iprops {
    selectedItem?: any;
    selectedType: 'Building' | 'Floor' | 'Location' | 'Room';
    loading: boolean;
    colorButton: string;
    t: TFunction;
    tableFilters: ItableFiltersReducer;
    saveAnyLocation: typeof saveAnyLocation;
    updateAnyLocation: typeof updateAnyLocation;
    toggleEditLocationModal: typeof toggleEditLocationModal;
    cloneAnyLocationObject: typeof cloneAnyLocationObject;
    facility: Ifacility;
    selectedBuilding: Ibuilding;
    selectedFloor: Ifloor;
    selectedLocation: Ilocation;
    show: boolean;
    title: string;
    isCloneMode?: boolean;
}

const MobileButtonGroup = styled.div`
    color: #000;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 24px;
`;

type IButtonGroupProps = {
    isMobile: boolean;
};

const ButtonGroup = ({ children, isMobile }: IButtonGroupProps & { children: React.ReactNode }) => {
    if (isMobile) {
        return <MobileButtonGroup>{children}</MobileButtonGroup>;
    }

    return <>{children}</>;
};

const ManageLocationForm: React.FC<Iprops & WithTranslation> = props => {
    const [formGroup, setFormGroup] = React.useState<FormGroup | any>();
    const isMobile = useSelector(getIsMobile);

    const handleClose = () => {
        props.toggleEditLocationModal();
    };

    const fieldConfig = React.useMemo(() => {
        const controls: any = {
            name: {
                options: {
                    validators: [FormUtil.validators.requiredWithTrim, Validators.maxLength(250)]
                },
                render: FormUtil.TextInput,
                meta: {
                    label: 'name',
                    colWidth: props.isCloneMode ? 9 : 12,
                    type: 'input',
                    autoFocus: true,
                    required: true
                }
            }
        };

        if (props.isCloneMode) {
            controls.quantity = {
                options: {
                    validators: [Validators.required, Validators.min(1), Validators.max(100)]
                },
                render: FormUtil.NumericInput,
                meta: {
                    label: 'quantity',
                    colWidth: 3,
                    required: true,
                    type: 'number',
                    min: 1,
                    max: 100
                }
            };
        }

        return { controls };
    }, [props.isCloneMode]);

    React.useEffect(() => {
        if (!props.selectedItem || !formGroup) {
            return;
        }

        forEach(props.selectedItem, (value, key) => {
            if (typeof value === 'string' && key.split('ID').length === 1) {
                // it is a string and did Not find 'ID'
                formGroup.patchValue({ [key]: value });
            }
        });

        // Set default quantity for clone mode
        if (props.isCloneMode) {
            formGroup.patchValue({ quantity: 1 });
        }
    }, [props.selectedItem, formGroup, props.isCloneMode]);

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (formGroup.status === 'INVALID') {
            formGroup.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }

        const { name, quantity } = formGroup.value;
        if (props.isCloneMode && props.selectedItem) {
            // clone the selected item with the custom name and quantity
            props.cloneAnyLocationObject(props.selectedItem, props.facility.id, props.t, name, quantity || 1);
            return;
        }
        if (props.selectedItem && props.selectedItem.id && props.selectedItem.id.length) {
            const newItem = {
                ...props.selectedItem,
                name
            };
            // updating a location object
            props.updateAnyLocation(newItem, props.facility.id);
            return;
        }
        // creating a new location
        props.saveAnyLocation(name, props.facility.id);
    };

    const setForm = (form: AbstractControl) => {
        const formGroupInstance = form;
        formGroupInstance.meta = {
            loading: props.loading
        };
        setFormGroup(formGroupInstance);
    };

    // get breadcrumb path
    // const getBreadcrumbs = () => {
    //     return (
    //         <Breadcrumb>
    //             {props.selectedBuilding.id ? <BreadcrumbItem active>{props.selectedBuilding.name}</BreadcrumbItem> : ''}
    //             {props.selectedFloor.id ? <BreadcrumbItem active>{props.selectedFloor.name}</BreadcrumbItem> : ''}
    //             {props.selectedLocation.id ? <BreadcrumbItem active>{props.selectedLocation.name}</BreadcrumbItem> : ''}
    //         </Breadcrumb>
    //     );
    // };

    return (
        <div className={isMobile ? 'location-table-modal-mobile' : undefined}>
            <Modal className={isMobile ? 'location-table-modal-mobile' : ''} show={props.show} onHide={handleClose}>
                <Modal.Header style={{ borderBottom: 'none' }}>
                    <Modal.Title>{props.title}</Modal.Title>
                    {isMobile ? (
                        <Button onClick={handleClose}>
                            <FontAwesomeIcon icon={['far', 'times']} size="lg" />
                        </Button>
                    ) : null}
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit}>
                        <Row>
                            <FormGenerator onMount={setForm} fieldConfig={fieldConfig} />
                        </Row>
                    </form>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: 'none' }}>
                    <ButtonGroup isMobile={isMobile as boolean}>
                        <Button
                            onClick={handleClose}
                            style={{
                                backgroundColor: '#fff',
                                borderColor: '#ccc',
                                color: '#000'
                            }}
                        >
                            {String(props.t('common:cancel'))}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={props.loading}
                            style={{
                                backgroundColor: '#FD9A24',
                                borderColor: '#C97B1D'
                            }}
                        >
                            {String(props.t('common:save'))}
                        </Button>
                    </ButtonGroup>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ManageLocationForm;
